import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { api, rupee } from "@/lib/api";

function sessionId() {
  let s = localStorage.getItem("hgk_chat_session");
  if (!s) { s = `s_${Math.random().toString(36).slice(2)}_${Date.now()}`; localStorage.setItem("hgk_chat_session", s); }
  return s;
}

const GREETING = {
  role: "assistant",
  content: "Namaste! I'm Kārigar, your handmade gift guide. Tell me who you're shopping for and the occasion — or ask me anything about handmadegiftkart.",
};
const CHIPS = ["Gift for my mother", "Something for Diwali", "Under ₹1000", "How do refunds work?"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sid = useRef(sessionId());

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sid.current, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, recommendations: data.recommendations }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't reach the makers just now. Please try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-[80] w-14 h-14 rounded-full bg-clay text-sand grid place-items-center shadow-[0_10px_30px_rgba(140,61,43,0.4)] hover:bg-forest transition-colors"
        data-testid="chat-toggle-btn" aria-label="Chat with Kārigar"
      >
        <AnimatePresence mode="wait">
          {open ? <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ opacity: 0 }}><X className="w-6 h-6" /></motion.span>
                : <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ opacity: 0 }}><MessageCircle className="w-6 h-6" /></motion.span>}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-5 z-[80] w-[calc(100vw-2.5rem)] sm:w-[380px] h-[560px] max-h-[75vh] bg-sand rounded-2xl border border-line shadow-[0_20px_60px_rgba(140,61,43,0.22)] flex flex-col overflow-hidden"
            data-testid="chat-window"
          >
            <div className="bg-forest text-sand px-5 py-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-terra grid place-items-center"><Sparkles className="w-5 h-5" /></div>
              <div>
                <div className="font-serif text-lg leading-none">Kārigar</div>
                <div className="text-[11px] text-sand/70 mt-0.5">Your handmade gift guide</div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[85%]">
                    <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      m.role === "user" ? "bg-clay text-sand rounded-br-sm" : "bg-surface text-ink rounded-bl-sm border border-line"
                    }`} data-testid={`chat-msg-${m.role}`}>
                      {m.content}
                    </div>
                    {m.recommendations?.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {m.recommendations.map((r) => (
                          <Link key={r.id} to={`/product/${r.id}`} onClick={() => setOpen(false)}
                            className="flex gap-3 items-center bg-white border border-line rounded-xl p-2 hover:border-clay transition-colors" data-testid={`chat-rec-${r.id}`}>
                            <img src={r.image} alt="" className="w-12 h-14 rounded-lg object-cover" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium leading-tight truncate">{r.title}</div>
                              <div className="text-xs text-mute">{rupee(r.price)} · {r.delivery_days}d</div>
                              <div className="text-[10px] text-clay">{r.seller_name}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start"><div className="bg-surface border border-line rounded-2xl rounded-bl-sm px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-clay" /></div></div>
              )}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {CHIPS.map((c) => (
                    <button key={c} onClick={() => send(c)} className="text-xs px-3 py-1.5 rounded-full border border-line hover:border-clay hover:text-clay transition-colors" data-testid={`chat-chip-${c.slice(0,6).replace(/\s/g,'-').toLowerCase()}`}>{c}</button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-line flex items-center gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Kārigar…"
                className="flex-1 bg-surface border border-line rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="chat-input" />
              <button type="submit" disabled={loading || !input.trim()} className="w-10 h-10 rounded-full bg-clay text-sand grid place-items-center disabled:opacity-50 hover:bg-forest transition-colors" data-testid="chat-send-btn" aria-label="Send">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
